import React, { useState, useEffect, useCallback, useRef } from 'react';
import { clickable } from '../../../utils/a11y';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaBriefcase, FaCalendarAlt, FaIdBadge, FaSignOutAlt,
  FaCamera, FaPaperclip, FaClock, FaCheckCircle, FaMapMarkerAlt,
  FaCommentAlt, FaUserCircle, FaChevronRight, FaTimes, FaBuilding,
  FaBars, FaHome, FaArrowRight, FaCertificate, FaTools
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole, isContractorAdmin } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import NotificationBell from '../../shared/NotificationBell';
import StatusBadge from '../../shared/JobStatusBadge';
import JobCalendar from '../../shared/JobCalendar';
import UserRoleBadge from '../../shared/UserRoleBadge';
import PropertyCard from '../../property/PropertyCard/PropertyCard';
import MyQualifications from './MyQualifications';
import JobInductionPack from '../Contractor/workspace/shared/JobInductionPack';
import Toolbox from '../Contractor/Toolbox';
import AssetTagger from '../Contractor/AssetTagger';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import '../Contractor/ContractorDashboard.css';
import './ContractorStaffDashboard.css';

const ContractorStaffDashboard = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [assignedProperties, setAssignedProperties] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [userName, setUserName] = useState('');
  const [userId, setUserId] = useState(null);
  const [userCode, setUserCode] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const cloudSafeId = userCode || (userId ? `CS-CONTRACTOR-${userId}` : '');

  useEffect(() => {
    if (getUserRole() !== 'contractor') { navigate('/login'); return; }
    if (isContractorAdmin()) {
      navigate('/contractor/dashboard', { replace: true });
      return;
    }
    fetchProfile();
    fetchJobs();
    fetchAssignedProperties();
    fetchCalendar();
  }, [navigate]);

  const fetchProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), { headers });
      if (res.ok) {
        const d = await res.json();
        setUserName(`${d.firstName} ${d.lastName}`);
        setUserId(d.id || null);
        setUserCode(d.userCode || null);
        setCompanyName(d.companyName || '');
        setCompanyCode(d.companyCode || '');
      }
    } catch (e) { /* silent */ }
  };

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/jobs'), { headers });
      if (res.ok) setJobs(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [headers]);

  const fetchAssignedProperties = useCallback(async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/contractor/assigned-properties'), { headers });
      if (res.ok) setAssignedProperties(await res.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchCalendar = useCallback(async () => {
    try {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      const res = await fetch(getApiEndpoint(`/api/jobs/calendar?start=${start}&end=${end}`), { headers });
      if (res.ok) setCalendarEvents(await res.json());
    } catch (e) { /* silent */ }
  }, [headers]);

  const fetchJobDetail = async (id) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/jobs/${id}`), { headers });
      if (res.ok) setSelectedJob(await res.json());
    } catch (e) { toast.error('Failed to load job'); }
  };

  const addNote = async () => {
    if (!noteText.trim() || !selectedJob) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/jobs/${selectedJob.id}/notes`), {
        method: 'POST', headers,
        body: JSON.stringify({ note_text: noteText, note_type: 'general' })
      });
      if (res.ok) {
        toast.success('Note added');
        setNoteText('');
        fetchJobDetail(selectedJob.id);
      }
    } catch (e) { toast.error('Failed to add note'); }
  };

  const uploadPhoto = async (e) => {
    if (!selectedJob || !e.target.files.length) return;
    const formData = new FormData();
    Array.from(e.target.files).forEach(f => formData.append('files', f));
    try {
      const res = await fetch(getApiEndpoint(`/api/jobs/${selectedJob.id}/attachments`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        toast.success('Photo uploaded');
        fetchJobDetail(selectedJob.id);
      }
    } catch (e) { toast.error('Failed to upload'); }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  const today = new Date().toISOString().split('T')[0];
  const todayJobs = jobs.filter(j => ['approved', 'active'].includes(j.status) && j.scheduled_date?.split('T')[0] === today);
  const upcomingJobs = jobs.filter(j => ['approved', 'active', 'invited'].includes(j.status) && j.scheduled_date?.split('T')[0] > today);
  const pastJobs = jobs.filter(j => j.status === 'completed');
  const activeJob = jobs.find(j => j.status === 'active');

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';

  const sectionLabels = {
    overview: 'Overview',
    jobs: 'My Jobs',
    calendar: 'Calendar',
    buildings: 'Buildings',
    qualifications: 'My Qualifications',
    toolbox: 'Toolbox',
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: FaHome },
    { id: 'jobs', label: 'Jobs', icon: FaBriefcase, badge: todayJobs.length || null },
    { id: 'calendar', label: 'Calendar', icon: FaCalendarAlt },
    { id: 'buildings', label: 'Buildings', icon: FaBuilding, badge: assignedProperties.length || null },
    { id: 'toolbox', label: 'Toolbox', icon: FaTools },
    { id: 'qualifications', label: 'My qualifications', icon: FaCertificate },
    { id: 'id-badge', label: 'ID Badge', icon: FaIdBadge, link: '/contractor/id-badge' },
  ];

  return (
    <div className="contractor-dashboard staff-dashboard">
      <header className="contractor-header">
        <div className="header-left">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="header-logo" />
          <div className="header-title-group">
            <h1 className="header-title">{sectionLabels[activeSection] || 'My Jobs'}</h1>
          </div>
        </div>
        <div className="header-right">
          <UserRoleBadge />
          {(companyName || companyCode) && (
            <span className="header-company-badge" title="Company you work for">
              <span className="header-badge-label">Company:</span>
              {companyName && <span className="header-company-name">{companyName}</span>}
              {companyCode && <span className="header-company-code">{companyName ? ` • ${companyCode}` : companyCode}</span>}
            </span>
          )}
          {cloudSafeId && (
            <span className="header-company-badge" title="Your Contractor ID">
              <span className="header-badge-label">Your ID:</span>
              <span className="header-contractor-id">{cloudSafeId}</span>
            </span>
          )}
          <NotificationBell />
          <div className="header-user" {...clickable(() => navigate('/contractor/profile'), { label: 'Open your profile' })}>
            <FaUserCircle className="header-user-icon" />
            <span className="header-user-name">{userName}</span>
          </div>
          <button className="header-logout" onClick={handleLogout}><FaSignOutAlt /></button>
        </div>
      </header>

      <div className="contractor-layout">
        <aside className={`contractor-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <nav className="sidebar-nav">
            {sidebarItems.map(item => (
              item.link ? (
                <Link
                  key={item.id}
                  to={item.link}
                  className={`sidebar-item ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="sidebar-icon" />
                  <span>{item.label}</span>
                  {item.badge && <span className="sidebar-badge">{item.badge}</span>}
                </Link>
              ) : (
                <button
                  key={item.id}
                  className={`sidebar-item ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
                >
                  <item.icon className="sidebar-icon" />
                  <span>{item.label}</span>
                  {item.badge && <span className="sidebar-badge">{item.badge}</span>}
                </button>
              )
            ))}
          </nav>
        </aside>

        <main className="contractor-main">
          {selectedJob ? (
            <JobView
              job={selectedJob}
              onBack={() => setSelectedJob(null)}
              noteText={noteText}
              setNoteText={setNoteText}
              onAddNote={addNote}
              onUploadPhoto={uploadPhoto}
            />
          ) : (
            <>
              {activeSection === 'overview' && renderOverview()}
              {activeSection === 'jobs' && renderJobs()}
              {activeSection === 'calendar' && renderCalendar()}
              {activeSection === 'buildings' && renderBuildings()}
              {activeSection === 'qualifications' && <MyQualifications />}
              {activeSection === 'toolbox' && <Toolbox onNavigate={setActiveSection} />}
              {activeSection === 'asset-tagger' && <AssetTagger />}
            </>
          )}
        </main>
      </div>

      {!selectedJob && (
        <div className="cs-bottom-nav">
          <div className="cs-bottom-nav-items">
            {sidebarItems.slice(0, 5).map(item => (
              item.link ? (
                <Link key={item.id} to={item.link} className={`cs-bottom-nav-item ${activeSection === item.id ? 'active' : ''}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <item.icon />
                  <span>{item.label}</span>
                  {item.badge && <span className="cs-bottom-nav-badge">{item.badge}</span>}
                </Link>
              ) : (
                <button key={item.id} className={`cs-bottom-nav-item ${activeSection === item.id ? 'active' : ''}`} onClick={() => setActiveSection(item.id)}>
                  <item.icon />
                  <span>{item.label}</span>
                  {item.badge && <span className="cs-bottom-nav-badge">{item.badge}</span>}
                </button>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );

  /* ─── Overview ─────────────────────────────── */
  function renderOverview() {
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
    const dateStr = new Date().toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    return (
      <div className="staff-overview">

        {/* Hero Welcome */}
        <div className="overview-hero">
          <div className="overview-hero-bg-circle overview-hero-bg-circle-1" />
          <div className="overview-hero-bg-circle overview-hero-bg-circle-2" />
          <div className="overview-hero-content">
            <p className="overview-greeting">{greeting}</p>
            <h1 className="overview-name">{userName || 'Welcome back'}</h1>
            {companyName && (
              <p className="overview-company">
                <FaBuilding style={{ fontSize: '0.7rem', opacity: 0.6 }} />
                {companyName}
              </p>
            )}
          </div>
          <div className="overview-hero-right">
            <p className="overview-date">{dateStr}</p>
            {cloudSafeId && (
              <span className="overview-id-pill">{cloudSafeId}</span>
            )}
          </div>
        </div>

        {/* Active Job Banner */}
        {activeJob && (
          <div className="overview-active-banner" {...clickable(() => fetchJobDetail(activeJob.id), { label: 'Open the job you are working on' })}>
            <div className="active-pulse" />
            <div className="active-info">
              <span className="active-label">ACTIVE JOB</span>
              <span className="active-title">{activeJob.title}</span>
              <span className="active-prop"><FaMapMarkerAlt /> {activeJob.property_name || 'On Site'}</span>
            </div>
            <FaChevronRight className="active-arrow" />
          </div>
        )}

        {/* Stats Row */}
        <div className="overview-stats">
          <div className="ov-stat-card ov-stat-today" {...clickable(() => setActiveSection('jobs'), { label: "Today's jobs" })}>
            <div className="ov-stat-icon"><FaBriefcase /></div>
            <div className="ov-stat-body">
              <span className="ov-stat-value">{todayJobs.length}</span>
              <span className="ov-stat-label">Today&apos;s Jobs</span>
            </div>
          </div>
          <div className="ov-stat-card ov-stat-upcoming" {...clickable(() => setActiveSection('jobs'), { label: 'Upcoming jobs' })}>
            <div className="ov-stat-icon"><FaCalendarAlt /></div>
            <div className="ov-stat-body">
              <span className="ov-stat-value">{upcomingJobs.length}</span>
              <span className="ov-stat-label">Upcoming</span>
            </div>
          </div>
          <div className="ov-stat-card ov-stat-done" {...clickable(() => setActiveSection('jobs'), { label: 'Completed jobs' })}>
            <div className="ov-stat-icon"><FaCheckCircle /></div>
            <div className="ov-stat-body">
              <span className="ov-stat-value">{pastJobs.length}</span>
              <span className="ov-stat-label">Completed</span>
            </div>
          </div>
          <div className="ov-stat-card ov-stat-buildings" {...clickable(() => setActiveSection('buildings'), { label: 'Your buildings' })}>
            <div className="ov-stat-icon"><FaBuilding /></div>
            <div className="ov-stat-body">
              <span className="ov-stat-value">{assignedProperties.length}</span>
              <span className="ov-stat-label">Buildings</span>
            </div>
          </div>
        </div>

        {/* Main Two-Column Grid */}
        <div className="overview-grid">
          {/* Today's Schedule */}
          <div className="ov-card">
            <div className="ov-card-header">
              <h3><FaBriefcase /> Today&apos;s Schedule</h3>
              <button className="ov-card-link" onClick={() => setActiveSection('jobs')}>
                View all <FaArrowRight />
              </button>
            </div>
            <div className="ov-card-body">
              {loading ? (
                <div className="ov-empty"><p>Loading...</p></div>
              ) : todayJobs.length > 0 ? (
                todayJobs.map(j => (
                  <div key={j.id} className="ov-job-row" {...clickable(() => fetchJobDetail(j.id))}>
                    <div className="ov-job-row-info">
                      <span className="ov-job-title">{j.title}</span>
                      <span className="ov-job-sub"><FaMapMarkerAlt /> {j.property_name || 'Property'}</span>
                    </div>
                    <StatusBadge status={j.status} size="small" />
                  </div>
                ))
              ) : (
                <div className="ov-empty">
                  <FaBriefcase />
                  <p>No jobs today</p>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Jobs */}
          <div className="ov-card">
            <div className="ov-card-header">
              <h3><FaCalendarAlt /> Upcoming Jobs</h3>
              <button className="ov-card-link" onClick={() => setActiveSection('calendar')}>
                Calendar <FaArrowRight />
              </button>
            </div>
            <div className="ov-card-body">
              {upcomingJobs.length > 0 ? (
                upcomingJobs.slice(0, 5).map(j => (
                  <div key={j.id} className="ov-job-row" {...clickable(() => fetchJobDetail(j.id))}>
                    <div className="ov-job-row-info">
                      <span className="ov-job-title">{j.title}</span>
                      <span className="ov-job-sub"><FaMapMarkerAlt /> {j.property_name || 'Property'}</span>
                    </div>
                    <span className="ov-job-date">{formatDate(j.scheduled_date)}</span>
                  </div>
                ))
              ) : (
                <div className="ov-empty">
                  <FaCalendarAlt />
                  <p>No upcoming jobs</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Buildings Row */}
        {assignedProperties.length > 0 && (
          <div className="ov-card">
            <div className="ov-card-header">
              <h3><FaBuilding /> Your Buildings</h3>
              <button className="ov-card-link" onClick={() => setActiveSection('buildings')}>
                View all <FaArrowRight />
              </button>
            </div>
            <div className="ov-buildings-row">
              {assignedProperties.slice(0, 6).map(p => (
                <div key={p.id} className="ov-building-chip" {...clickable(() => navigate(`/propertydashboard/${p.id}`))}>
                  <FaBuilding />
                  <span>{p.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recently Completed */}
        {pastJobs.length > 0 && (
          <div className="ov-card">
            <div className="ov-card-header">
              <h3><FaCheckCircle /> Recently Completed</h3>
              <button className="ov-card-link" onClick={() => setActiveSection('jobs')}>
                View all <FaArrowRight />
              </button>
            </div>
            <div className="ov-card-body">
              {pastJobs.slice(0, 3).map(j => (
                <div key={j.id} className="ov-job-row" {...clickable(() => fetchJobDetail(j.id))}>
                  <div className="ov-job-row-info">
                    <span className="ov-job-title">{j.title}</span>
                    <span className="ov-job-sub"><FaMapMarkerAlt /> {j.property_name || 'Property'}</span>
                  </div>
                  <div className="ov-completed-meta">
                    {j.duration_minutes != null && (
                      <span className="ov-duration"><FaClock /> {Math.floor(j.duration_minutes / 60)}h {j.duration_minutes % 60}m</span>
                    )}
                    <span className="ov-job-date">{formatDate(j.scheduled_date)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    );
  }

  /* ─── Jobs ─────────────────────────────────── */
  function renderJobs() {
    return (
      <div className="staff-jobs-section">
        {activeJob && (
          <div className="staff-active-banner" {...clickable(() => fetchJobDetail(activeJob.id), { label: 'Open the job you are working on' })}>
            <div className="active-pulse" />
            <div className="active-info">
              <span className="active-label">ACTIVE JOB</span>
              <span className="active-title">{activeJob.title}</span>
              <span className="active-prop"><FaMapMarkerAlt /> {activeJob.property_name || 'On Site'}</span>
            </div>
            <FaChevronRight className="active-arrow" />
          </div>
        )}

        <div className="staff-section-header">
          <h2 className="section-title">Today&apos;s Jobs</h2>
          <span className="staff-section-count">{todayJobs.length} job{todayJobs.length !== 1 ? 's' : ''}</span>
        </div>
        {loading ? <div className="staff-loading">Loading...</div> : (
          todayJobs.length > 0 ? todayJobs.map(j => (
            <div key={j.id} className="staff-job-card" {...clickable(() => fetchJobDetail(j.id))}>
              <div className="staff-job-top">
                <h3>{j.title}</h3>
                <StatusBadge status={j.status} size="small" />
              </div>
              <div className="staff-job-meta">
                <span><FaMapMarkerAlt /> {j.property_name || 'Property'}</span>
                <span><FaClock /> {j.area}</span>
              </div>
            </div>
          )) : (
            <div className="staff-empty">
              <FaBriefcase />
              <p>No jobs scheduled for today</p>
            </div>
          )
        )}

        <div className="staff-section-header" style={{ marginTop: '1.75rem' }}>
          <h2 className="section-title">Upcoming Jobs</h2>
          <span className="staff-section-count">{upcomingJobs.length} job{upcomingJobs.length !== 1 ? 's' : ''}</span>
        </div>
        {upcomingJobs.length > 0 ? upcomingJobs.map(j => (
          <div key={j.id} className="staff-job-card" {...clickable(() => fetchJobDetail(j.id))}>
            <div className="staff-job-top">
              <h3>{j.title}</h3>
              <StatusBadge status={j.status} size="small" />
            </div>
            <div className="staff-job-meta">
              <span><FaMapMarkerAlt /> {j.property_name || 'Property'}</span>
              <span><FaCalendarAlt /> {formatDate(j.scheduled_date)}</span>
            </div>
          </div>
        )) : <div className="staff-empty"><FaCalendarAlt /><p>No upcoming jobs</p></div>}

        <div className="staff-section-header" style={{ marginTop: '1.75rem' }}>
          <h2 className="section-title">Completed Jobs</h2>
          <span className="staff-section-count">{pastJobs.length} job{pastJobs.length !== 1 ? 's' : ''}</span>
        </div>
        {pastJobs.length > 0 ? pastJobs.map(j => (
          <div key={j.id} className="staff-job-card" {...clickable(() => fetchJobDetail(j.id))}>
            <div className="staff-job-top">
              <h3>{j.title}</h3>
              <StatusBadge status={j.status} size="small" />
            </div>
            <div className="staff-job-meta">
              <span><FaMapMarkerAlt /> {j.property_name || 'Property'}</span>
              <span><FaCalendarAlt /> {formatDate(j.scheduled_date)}</span>
              {j.duration_minutes != null && <span><FaClock /> {Math.floor(j.duration_minutes / 60)}h {j.duration_minutes % 60}m</span>}
            </div>
          </div>
        )) : <div className="staff-empty"><FaCheckCircle /><p>No completed jobs yet</p></div>}
      </div>
    );
  }

  /* ─── Calendar ──────────────────────────────── */
  function renderCalendar() {
    return (
      <div className="staff-calendar-section">
        <h2 className="section-title">Calendar</h2>
        <div className="calendar-legend" style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span className="legend-item"><span className="legend-dot" style={{ background: '#e0e7ff' }} /> Invited</span>
          <span className="legend-item"><span className="legend-dot" style={{ background: '#fef3c7' }} /> Quoted</span>
          <span className="legend-item"><span className="legend-dot" style={{ background: '#d1fae5' }} /> Approved</span>
          <span className="legend-item"><span className="legend-dot" style={{ background: '#dbeafe' }} /> Active</span>
        </div>
        <JobCalendar
          events={calendarEvents}
          onEventClick={(ev) => fetchJobDetail(ev.id)}
          onDateChange={(start, end) => {
            fetch(getApiEndpoint(`/api/jobs/calendar?start=${start}&end=${end}`), { headers })
              .then(r => r.ok ? r.json() : []).then(setCalendarEvents);
          }}
        />
      </div>
    );
  }

  /* ─── Buildings ─────────────────────────────── */
  function renderBuildings() {
    return (
      <div className="staff-buildings-section">
        <h2 className="section-title">Buildings</h2>
        <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
          Properties you have access to through your assigned jobs.
        </p>
        {assignedProperties.length === 0 ? (
          <div className="staff-empty">
            <FaBuilding />
            <p>No buildings yet</p>
            <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>When you&apos;re assigned to a job at a property, it will appear here.</p>
          </div>
        ) : (
          <div className="properties-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.25rem' }}>
            {assignedProperties.map(property => (
              <PropertyCard
                key={property.id}
                property={property}
                onDelete={null}
                onPropertyClick={(id) => navigate(`/propertydashboard/${id}`)}
                viewOnly={true}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
};

/* ─── Job View ───────────────────────────────── */
const JobView = ({ job, onBack, noteText, setNoteText, onAddNote, onUploadPhoto }) => {
  const fileInputRef = useRef(null);
  const [elapsed, setElapsed] = useState(null);

  useEffect(() => {
    if (!job?.started_at || job.completed_at) return;
    const update = () => {
      const start = new Date(job.started_at).getTime();
      const now = Date.now();
      const mins = Math.floor((now - start) / 60000);
      setElapsed(mins);
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, [job?.started_at, job?.completed_at]);

  const durationDisplay = job.duration_minutes != null
    ? `${Math.floor(job.duration_minutes / 60)}h ${job.duration_minutes % 60}m`
    : elapsed != null
      ? `${Math.floor(elapsed / 60)}h ${elapsed % 60}m`
      : null;

  return (
    <div className="staff-job-view">
      <button className="staff-back-btn" onClick={onBack}><FaTimes /> Back</button>

      <div className="staff-job-view-header">
        <h2>{job.title}</h2>
        <StatusBadge status={job.status} />
      </div>

      <div className="staff-job-view-info">
        <div className="info-row"><FaMapMarkerAlt /> <span>{job.property_name}</span></div>
        <div className="info-row"><FaCalendarAlt /> <span>{new Date(job.scheduled_date).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}</span></div>
        <div className="info-row"><FaBriefcase /> <span>{job.area}</span></div>
        {job.started_at && (
          <div className="info-row">
            <FaClock /> <span>
              Started {new Date(job.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              {durationDisplay && ` • ${durationDisplay}`}
            </span>
          </div>
        )}
        {job.completed_at && job.duration_minutes != null && (
          <div className="info-row"><FaCheckCircle /> <span>Completed in {Math.floor(job.duration_minutes / 60)}h {job.duration_minutes % 60}m</span></div>
        )}
      </div>

      {job.description && <p className="staff-job-desc">{job.description}</p>}

      {/* Client induction, PPE, pre-site checks, documents and forms for this job (FSM 3.8) */}
      <JobInductionPack jobId={job.id} variant="field" />

      {['approved', 'active'].includes(job.status) && (
        <div className="staff-actions">
          <button className="staff-action-btn" onClick={() => fileInputRef.current?.click()}>
            <FaCamera /> Upload Photo
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={onUploadPhoto} />
        </div>
      )}

      {['approved', 'active', 'completed'].includes(job.status) && (
        <div className="staff-notes-section">
          <h3><FaCommentAlt /> Notes</h3>
          <div className="staff-note-input">
            <input placeholder="Add a note or log an issue..." value={noteText} onChange={e => setNoteText(e.target.value)} />
            <button onClick={onAddNote} disabled={!noteText.trim()}>Send</button>
          </div>
          {job.notes && job.notes.map(n => (
            <div key={n.id} className="staff-note-item">
              <div className="staff-note-header">
                <FaUserCircle />
                <span className="staff-note-author">{n.first_name} {n.last_name}</span>
                <span className="staff-note-time">{new Date(n.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <p className="staff-note-text">{n.note_text}</p>
            </div>
          ))}
        </div>
      )}

      {job.attachments && job.attachments.length > 0 && (
        <div className="staff-attachments">
          <h3><FaPaperclip /> Attachments ({job.attachments.length})</h3>
          <div className="staff-attachment-grid">
            {job.attachments.map(a => {
              const docUrl = (a.download_url ? getApiEndpoint(a.download_url) : undefined);
              return (
                <a key={a.id} href={docUrl} target="_blank" rel="noopener noreferrer" className="staff-attachment-item staff-attachment-link">
                  {a.mime_type?.startsWith('image/') ? (
                    <img src={docUrl} alt={a.original_filename} />
                  ) : (
                    <div className="attachment-file"><FaPaperclip /><span>{a.original_filename}</span></div>
                  )}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {job.staff && job.staff.length > 0 && (
        <div className="staff-team">
          <h3>Team on Job</h3>
          {job.staff.map(s => (
            <div key={s.id} className="staff-team-member">
              <FaUserCircle />
              <div>
                <span className="member-name">{s.first_name} {s.last_name}</span>
                <span className="member-status">
                  {s.signed_in_at ? (s.signed_out_at ? 'Signed out' : 'On site') : 'Not signed in'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractorStaffDashboard;
